/**
 * usePageTracker hook
 * Sends a page visit event to the backend for funnel analytics.
 * Uses sessionStorage to maintain a consistent session ID.
 * Captures UTM parameters and ad IDs from the landing URL.
 * Fires Meta Pixel PageView + QuizStep events on each SPA route change.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { newEventId, getFbCookies } from '../utils/metaEvents';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const UTM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign',
  'utm_content', 'utm_term',
  'ad_id', 'campaign_id', 'adset_id',
] as const;

const STEP_MAP: Record<string, { name: string; type: string }> = {
  '/quiz/q1-duration':                         { name: 'Q1 Duration',                  type: 'question' },
  '/quiz/pain-duration-education':             { name: 'Q1 Pain Duration Education',   type: 'education' },
  '/quiz/q2-treatments':                       { name: 'Q2 Treatments',                type: 'question' },
  '/quiz/q3-conditions':                       { name: 'Q3 Conditions',                type: 'question' },
  '/quiz/q4-whats-missing':                    { name: 'Q4 Whats Missing',             type: 'question' },
  '/quiz/q5-urgency':                          { name: 'Q5 Urgency',                   type: 'question' },
  '/quiz/q6-annual-spending':                  { name: 'Q6 Annual Spending',            type: 'question' },
  '/quiz/q7-open-questions':                   { name: 'Q7 Open Questions',             type: 'question' },
  '/quiz/connecting-message-q2':               { name: 'Connecting Message',            type: 'education' },
  '/quiz/education-q2a':                       { name: 'Education Q2A',                 type: 'education' },
  '/quiz/education-q2a-none':                  { name: 'Education Q2A None',            type: 'education' },
  '/quiz/primary-cell-explanation':            { name: 'Primary Cell Explanation',       type: 'education' },
  '/quiz/alternative-primary-cell-explanation': { name: 'Alt Primary Cell Explanation', type: 'education' },
  '/quiz/congratulations':                     { name: 'Congratulations',               type: 'conversion' },
  '/quiz/congratulations-alternative':         { name: 'Congratulations Alternative',   type: 'conversion' },
  '/quiz/welcome':                             { name: 'Welcome',                       type: 'conversion' },
  '/quiz/disqualified-too-soon':               { name: 'Disqualified Too Soon',         type: 'disqualification' },
  '/quiz/disqualified-non-treatable':          { name: 'Disqualified Non-Treatable',    type: 'disqualification' },
  '/quiz/manual-review':                       { name: 'Manual Review',                 type: 'disqualification' },
  '/quiz/thank-you-non-treatable':             { name: 'Thank You Non-Treatable',       type: 'disqualification' },
  '/quiz/terms-and-conditions':                { name: 'Terms and Conditions',           type: 'legal' },
  '/quiz/privacy-policy':                      { name: 'Privacy Policy',                 type: 'legal' },
};

function getSessionId(): string {
  let id = sessionStorage.getItem('analytics_session_id');
  if (!id) {
    id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem('analytics_session_id', id);
  }
  return id;
}

function captureUtmParams(): void {
  const params = new URLSearchParams(window.location.search);
  const hasUtm = UTM_KEYS.some((k) => params.has(k));
  if (!hasUtm) return;

  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) sessionStorage.setItem(`analytics_${key}`, val);
  }
}

function getStoredUtm(): Record<string, string> {
  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const val = sessionStorage.getItem(`analytics_${key}`);
    if (val) utm[key] = val;
  }
  return utm;
}

function trackMetaPixel(pathname: string): { pageviewEventId: string; step?: { name: string; type: string }; quizstepEventId?: string } {
  const pageviewEventId = newEventId('pv');
  const step = STEP_MAP[pathname];
  let quizstepEventId: string | undefined;

  if (window.fbq) {
    // Fire PageView on every SPA navigation so URL-based custom conversions trigger
    window.fbq('track', 'PageView', {}, { eventID: pageviewEventId });

    // Fire a custom QuizStep event with step details for granular ad reporting
    if (step) {
      quizstepEventId = newEventId('qs');
      window.fbq('trackCustom', 'QuizStep', {
        step_name: step.name,
        step_path: pathname,
        step_type: step.type,
      }, { eventID: quizstepEventId });
    }
  }

  return { pageviewEventId, step, quizstepEventId };
}

export function usePageTracker() {
  const location = useLocation();

  useEffect(() => {
    captureUtmParams();
  }, []);

  useEffect(() => {
    // Meta Pixel tracking (browser) — returns event IDs to dedup with server CAPI
    const { pageviewEventId, step, quizstepEventId } = trackMetaPixel(location.pathname);

    // Backend analytics tracking + Meta CAPI mirror
    const sessionId = getSessionId();
    const utm = getStoredUtm();
    const { fbp, fbc } = getFbCookies();

    fetch('/api/analytics/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        page: location.pathname,
        referrer: document.referrer || null,
        event_source_url: window.location.href,
        pageview_event_id: pageviewEventId,
        quizstep_event_id: quizstepEventId,
        quizstep_name: step?.name,
        quizstep_type: step?.type,
        fbp,
        fbc,
        ...utm,
      }),
    }).catch(() => {
      // Silently fail — analytics should never break the quiz
    });
  }, [location.pathname]);
}
